<script setup>
/**
 * @todo Change to fixed sizes
 *
 */
const router = useRouter();

const props = defineProps({
  id: {
    type: String,
    required: false,
  },
  variant: {
    type: String,
    default: "",
    required: false,
    validator(value) {
      return [
        "",
        "primary",
        "secondary",
        "success",
        "danger",
        "default-danger",
        "default-success",
        "outline-primary",

        // Darken
        "darken-success",

        // Ghost
        "ghost",
      ].includes(value);
    },
  },
  disabled: {
    required: false,
    default: false,
  },
  icon: {
    type: String,
    required: false,
  },
  iconAfter: {
    type: String,
    required: false,
  },
  size: {
    type: String,
    default: "md",
    required: false,
    validator(value) {
      return ["xs", "sm", "md", "lg", "xl"].includes(value);
    },
  },
  loading: {
    type: Boolean,
    default: false,
    required: false,
  },
  completed: {
    type: Boolean,
    default: false,
    required: false,
  },
  to: {
    type: String,
    required: false,
    default: "",
  },
  active: {
    type: Boolean,
    required: false,
  },
});

function navigateTo() {
  if (props.to) {
    router.push(props.to);
  }
}
</script>
<template>
  <button
    v-if="id"
    :id="id"
    type="button"
    class="relative inline-flex cursor-pointer items-center justify-center overflow-hidden whitespace-nowrap rounded-md border border-transparent font-medium leading-none outline-transparent transition-all duration-300 ease-in-out before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:bg-white/10 before:opacity-0 before:transition-all before:content-['']"
    :disabled="disabled"
    :class="{
      // Variants
      'text-brand-primary': active,

      // Primary
      'border-brand-primary bg-brand-primary hover:bg-brighten-brand-primary':
        variant === 'primary',

      // Primary darken
      'border-darken-brand-primary bg-darken-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white':
        variant === 'secondary',
      'outline !outline-brand/50': variant === 'secondary' && active,
      'border-red-500 bg-red-500 hover:brightness-125': variant === 'danger',
      'border-green-500 bg-green-500': variant === 'success',

      // Variants darken
      'border-darken-primary bg-darken-primary text-brand':
        variant === 'darken-primary',
      'border-darken-success bg-darken-success text-success hover:before:!opacity-50':
        variant === 'darken-success',

      // Secondary
      'border-green-500/10 bg-green-500/10 text-green-500':
        variant === 'secondary-success',
      'border-red-500/10 bg-red-500/10 text-red-500':
        variant === 'secondary-danger',

      // Default variants
      'bg-transparent hover:bg-red-500': variant === 'default-danger',
      'bg-transparent hover:bg-green-500': variant === 'default-success',

      // Ghost
      'bg-transparent text-brand-primary !border-none': variant === 'ghost',

      // Outline variants
      'border-brand-primary bg-transparent text-brand-primary hover:bg-brand':
        variant === 'outline-primary',

      // Disabled
      '!cursor-not-allowed brightness-50': disabled,

      // Sizes
      'px-2 sm:px-5': size === '',
      '!py-2 px-3 text-xs': size === 'xs',
      'px-4 py-3 text-sm': size === 'sm',
      'text-md px-4 py-3': size === 'md',
      'px-6 py-4 text-lg': size === 'lg',
      'px-10 py-6 text-2xl': size === 'xl',

      // States
      'bg-success !text-white': variant === 'darken' && completed,
    }"
    @click="navigateTo()"
  >
    <span
      :class="{ 'opacity-0': loading || completed }"
      class="inline-flex items-center gap-2"
    >
      <Icon v-if="icon" :name="icon" class="text-[.8em]" />
      <slot />
      <Icon v-if="iconAfter" :name="iconAfter" class="text-[.8em]" />
    </span>
    <Icon
      v-if="loading"
      name="fa6-solid:circle-notch"
      class="absolute animate-spin"
    />
    <Icon v-if="completed" name="fa6-solid:circle-check" class="absolute" />
  </button>
  <button
    v-else
    type="button"
    class="relative inline-flex cursor-pointer items-center justify-center overflow-hidden whitespace-nowrap rounded-md border border-transparent font-medium leading-none outline-transparent transition-all duration-300 ease-in-out before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:bg-white/10 before:opacity-0 before:transition-all before:content-['']"
    :disabled="disabled"
    :class="{
      // Variants
      'text-brand-primary': active,

      // Primary
      'border-brand-primary bg-brand-primary hover:bg-brighten-brand-primary':
        variant === 'primary',

      // Primary darken
      'border-darken-brand-primary bg-darken-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white':
        variant === 'secondary',
      'outline !outline-brand/50': variant === 'secondary' && active,
      'border-red-500 bg-red-500': variant === 'danger',
      'border-green-500 bg-green-500': variant === 'success',

      // Variants darken
      'border-darken-primary bg-darken-primary text-brand':
        variant === 'darken-primary',
      'border-darken-success bg-darken-success text-success hover:before:!opacity-50':
        variant === 'darken-success',

      // Secondary
      'border-green-500/10 bg-green-500/10 text-green-500':
        variant === 'secondary-success',
      'border-red-500/10 bg-red-500/10 text-red-500':
        variant === 'secondary-danger',

      // Default variants
      'bg-transparent hover:bg-red-500': variant === 'default-danger',
      'bg-transparent hover:bg-green-500': variant === 'default-success',

      // Outline variants
      'border-brand-primary bg-transparent text-brand-primary hover:bg-brand':
        variant === 'outline-primary',

      // Disabled
      '!cursor-not-allowed brightness-50': disabled,

      // Sizes
      'px-2 sm:px-5': size === '',
      '!py-2 px-3 text-xs': size === 'xs',
      'px-4 py-3 text-sm': size === 'sm',
      'text-md px-4 py-3': size === 'md',
      'px-6 py-4 text-lg': size === 'lg',
      'px-10 py-6 text-2xl': size === 'xl',

      // States
      'bg-success !text-white': variant === 'darken' && completed,
    }"
    @click="navigateTo()"
  >
    <span
      :class="{ 'opacity-0': loading || completed }"
      class="inline-flex items-center gap-2"
    >
      <Icon v-if="icon" :name="icon" class="text-[.8em]" />
      <slot />
      <Icon v-if="iconAfter" :name="iconAfter" class="text-[.8em]" />
    </span>
    <Icon
      v-if="loading"
      name="fa6-solid:circle-notch"
      class="absolute animate-spin"
    />
    <Icon v-if="completed" name="fa6-solid:circle-check" class="absolute" />
  </button>
</template>
